import { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client';

import { LmsHomeworkSubmissionDownloadUrlQuery } from 'core/operations';
import type {
  LmsHomeworkSubmissionDownloadUrlQuery as IDownloadUrlQueryResult,
  LmsHomeworkSubmissionDownloadUrlQueryVariables,
} from 'core/graphql';

/**
 * Fetches a short-TTL presigned GET link for a ZIP submission. Always
 * network-only — a cached presigned URL is expired by definition, so this
 * deliberately bypasses useQuery/cache entirely.
 *
 * Network errors propagate as thrown ApolloError — callers handle the UX.
 */
export const useHomeworkDownloadUrl = () => {
  const client = useApolloClient();
  const [loading, setLoading] = useState(false);

  const getDownloadUrl = useCallback(
    async (submissionId: string): Promise<string | null> => {
      setLoading(true);
      try {
        const { data } = await client.query<
          IDownloadUrlQueryResult,
          LmsHomeworkSubmissionDownloadUrlQueryVariables
        >({
          query: LmsHomeworkSubmissionDownloadUrlQuery,
          variables: { submissionId },
          fetchPolicy: 'network-only',
        });
        return data?.homeworkSubmissionDownloadUrl ?? null;
      } finally {
        setLoading(false);
      }
    },
    [client],
  );

  return { getDownloadUrl, loading };
};
